import React from "react";
import { SiteNav, SiteStickyFooter } from "./SiteChrome.js";
import SiteHero from "./SiteHero.js";
import SiteBackground from "./SiteBackground.js";
import { AD_MODE } from "../lib/site-config.js";

const FEATURED_GAME = {
  name: "Stack Wars",
  blurb: "Drop your pieces, lock in combos, and send your opponent spiraling into chaos.",
  icon: "🏗️",
  href: "/stack-wars",
  num: "01",
};

const MINI_GAMES = [
  { name: "Tic-Tac-Toe",        blurb: "The classic 3-in-a-row duel — solo or vs friend.",         icon: "⭕", href: "/tic-tac-toe",    accent: "#ff4d8d", num: "02" },
  { name: "Memory Match",       blurb: "Flip cards, find pairs, test your recall.",                  icon: "🃏", href: "/memory-match",   accent: "#a855f7", num: "03" },
  { name: "Ladoo",              blurb: "Roll dice, race tokens home, capture rivals.",               icon: "🎲", href: "/ladoo",          accent: "#ffd44d", num: "04" },
  { name: "Checkers",           blurb: "Jump, capture, crown your kings in this classic board duel. ", icon: "🔴", href: "/checkers",      accent: "#e74c3c", num: "05" },
  { name: "Hickro",             blurb: "Match cards by color or number. Action cards chaos!",        icon: "🃏", href: "/hickro",         accent: "#2ecc71", num: "06" },
  { name: "ParaTrooper",        blurb: "Blast paratroopers out of the sky before they land!",        icon: "🪂", href: "/paratrooper",   accent: "#f39c12", num: "07" },
  { name: "Tank Wars",          blurb: "Aim, fire, and destroy the enemy tank with physics!",        icon: "🛞",  href: "/tank-wars",     accent: "#e67e22", num: "08" },
  { name: "Carrom",             blurb: "Flick discs into pockets in this classic board game.",       icon: "🎯", href: "/carrom",         accent: "#8e44ad", num: "09" },
  { name: "Pool",               blurb: "Rack 'em up! Sink balls, avoid fouls, win the game.",        icon: "🎱", href: "/pool",           accent: "#2ecc71", num: "10" },
  { name: "Block By Block",     blurb: "Stack blocks, clear lines, and chase that high score!",      icon: "🧱", href: "/block-by-block", accent: "#00f0f0", num: "11" },
  { name: "Dodgeball",          blurb: "Aim, shoot, dodge! Bouncing balls of fury.",                 icon: "🤾", href: "/dodgeball",     accent: "#e74c3c", num: "12" },
  { name: "Air Hockey",         blurb: "Paddle, deflect, score! Air hockey with a glowing puck.",    icon: "🏒", href: "/air-hockey",    accent: "#3498db", num: "13" },
  { name: "Snake",              blurb: "Eat, grow, survive. Classic arcade snake with neon visuals.", icon: "🐍", href: "/snake",         accent: "#00ffea", num: "14" },
  { name: "Chess",              blurb: "Outsmart your opponent in the classic strategy board game.", icon: "♚", href: "/chess",         accent: "#00b4ff", num: "15" },
  { name: "Hangman",            blurb: "Guess the word before the hangman is complete!",            icon: "💀", href: "/hangman",       accent: "#ff4d8d", num: "16" },
];

const TICKER_ITEMS = [
  "Stack Wars", "Tic-Tac-Toe", "Memory Match", "Ladoo",
  "Checkers", "Hickro", "ParaTrooper", "Tank Wars", "Carrom", "Pool", "Block By Block", "Dodgeball", "Air Hockey", "Snake", "Chess", "Hangman",
];

const CREW = [
  { emoji: "😤", name: "Spark", bg: "linear-gradient(160deg,#ffe57f,#ff9f44)", glow: "#ffb347" },
  { emoji: "⚡",  name: "Bolt",  bg: "linear-gradient(160deg,#93ffcf,#26c9a1)", glow: "#38f2b8" },
  { emoji: "🫧",  name: "Bloop", bg: "linear-gradient(160deg,#9fbcff,#7a76ff)", glow: "#a855f7" },
];

export default function MisterGamesPage() {
  return (
    <>
      <SiteBackground />

      <div className="site-wrap">
        <SiteNav />

        <SiteHero
          title="Simple Games, Big Fun"
          subtitle="No downloads. No rules. Just pure browser-based fun. Smack talk the competition."
          ctaLabel="Play Stack Wars"
          ctaHref="/stack-wars"
          ctaSecondaryLabel="All Games"
          ctaSecondaryHref="/#games"
        />

        {/* ── Game Select ──────────────────────────────────────────── */}
        <section className="game-select" id="games">

          {/* Scrolling ticker */}
          <div className="ticker-strip" aria-hidden="true">
            <div className="ticker-inner">
              {Array.from({ length: 8 }).map((_, rep) =>
                TICKER_ITEMS.map((name) => (
                  <React.Fragment key={`${rep}-${name}`}>
                    <span className="ticker-item">{name}</span>
                    <span className="ticker-sep">★</span>
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          {/* Section header */}
          <div className="gs-header">
            <div>
              <p className="gs-eyebrow">// Select Your Stage</p>
              <h2 className="gs-title">
                Choose Your<span className="gs-title-accent"> Game</span>
                <span className="gs-cursor" aria-hidden="true" />
              </h2>
            </div>
            <div className="gs-subline">16 games live · no account needed</div>
          </div>

          {/* Grid: 1 featured + 4 mini */}
          <div className="gs-grid">

            {/* Featured card */}
            <article className="feat-card">
              <div className="feat-card-glow" />
              <div className="feat-card-diag" />
              <span className="feat-card-num">{FEATURED_GAME.num}</span>

              <div className="feat-card-icon-area">
                <div className="feat-card-icon-circle">
                  {FEATURED_GAME.icon}
                </div>
              </div>

              <div className="feat-card-label">Featured</div>
              <h3 className="feat-card-name">{FEATURED_GAME.name}</h3>
              <p className="feat-card-blurb">{FEATURED_GAME.blurb}</p>
              <a className="feat-card-cta" href={FEATURED_GAME.href}>▶ Play Now</a>
            </article>

            {/* Mini cards */}
            {MINI_GAMES.map((g) => (
              <article
                key={g.name}
                className="mini-card"
                style={{
                  "--mini-accent":        g.accent,
                  "--mini-accent-bg":     `${g.accent}1e`,
                  "--mini-accent-shadow": `${g.accent}66`,
                } as React.CSSProperties}
              >
                <div className="mini-card-accent-bar" style={{ background: g.accent }} />
                <span className="mini-card-num">{g.num}</span>
                <div
                  className="mini-card-icon"
                  style={{
                    border: `1px solid ${g.accent}45`,
                    boxShadow: `0 0 24px ${g.accent}28, inset 0 0 24px ${g.accent}0a`,
                    background: `radial-gradient(circle at center, ${g.accent}18 0%, ${g.accent}06 55%, transparent 80%)`,
                  }}
                >
                  {g.icon}
                </div>
                <h3 className="mini-card-name">{g.name}</h3>
                <p className="mini-card-blurb">{g.blurb}</p>
                <a className="mini-card-cta" href={g.href}>▶ Play</a>
              </article>
            ))}

          </div>
        </section>

        {/* ── Meet the Crew ────────────────────────────────────────── */}
        <section className="character-panel" id="crew">
          <p className="gs-eyebrow">// The Crew</p>
          <h2>Meet the Characters</h2>
          <p>Spark, Bolt, and Bloop — your chaotic guides to MisterGames.</p>

          <div className="crew-emoji-row">
            {CREW.map((c) => (
              <div
                key={c.name}
                className="crew-emoji-box"
                style={{
                  background: c.bg,
                  border: `2px solid ${c.glow}`,
                  boxShadow: `0 0 30px ${c.glow}60`,
                }}
              >
                {c.emoji}
              </div>
            ))}
          </div>

          <div className="crew-names">
            {CREW.map((c) => (
              <div key={c.name} className="crew-name">{c.name}</div>
            ))}
          </div>
        </section>

        <SiteStickyFooter adMode={AD_MODE} />
      </div>
    </>
  );
}
